/**
 * PostHog analytics integration for MAN-34.
 * Tracks product usage with organization and environment context.
 *
 * Core events implemented:
 * - user_signed_up: New user completes signup (method)
 * - user_signed_in: Returning user signs in (method)
 * - user_logged_in: Alias for user_signed_in; used by the ops dashboard (method, user_role)
 * - user_invited: Org member or external collaborator is invited (invite_type, role)
 * - dashboard_viewed: User views home or SOP list (tab_name)
 * - sop_file_uploaded: File(s) submitted for SOP analysis (type: "new_sop"|"regenerate_sop", flow_type, file_count)
 * - sop_created: New SOP record created after successful analysis (sop_id, flow_type)
 * - sop_analysis_started: User starts AI analysis (flow_type, sop_type, file_count, source)
 * - sop_version_completed: User completes/releases an SOP version (sop_id, version_id)
 * - sop_proposed_version_generated: Super-admin triggers AI regeneration (sop_id, version_id, step_count)
 * - sop_proposed_version_responded: User accepts or rejects a proposed step (sop_id, user_decision: "accepted"|"rejected")
 * - sop_downloaded: User downloads SOP as PDF or Markdown (format)
 * - app_error_occurred: An application error was caught (error_type, error_location, severity)
 *
 * All events include super properties: organization, org_id, environment, team.
 */

import posthog from "posthog-js";

const isEnabled = (): boolean =>
  Boolean(typeof window !== "undefined" && import.meta.env.VITE_POSTHOG_KEY);

/** Derive organization from hostname (e.g. motorola.manufacture-ai.com → Motorola). */
export function getOrganizationFromHostname(): string {
  if (typeof window === "undefined") return "unknown";
  const host = window.location.hostname;
  if (host.includes("demo.")) return "Demo";
  if (host.includes("motorola.")) return "Motorola";
  if (host.includes("forvia.")) return "Forvia";
  if (host.includes("launchflow.")) return "Launchflow";
  if (host === "localhost" || host.startsWith("127.")) return "development";
  return host.split(".")[0] || "unknown";
}

/** Derive environment from Vite mode and API URL. */
function getEnvironment(): string {
  const mode = import.meta.env.MODE;
  const apiUrl = import.meta.env.VITE_API_URL ?? "";
  if (apiUrl.includes("localhost") || apiUrl.includes("127.0.0.1")) return "development";
  if (apiUrl.includes("staging") || mode === "staging") return "staging";
  if (mode === "production") return "production";
  return mode || "development";
}

/** Register super properties sent with every event. */
export function registerPostHogSuperProperties(): void {
  if (!isEnabled()) return;
  const organization =
    import.meta.env.VITE_ORGANIZATION || getOrganizationFromHostname();
  const environment = import.meta.env.VITE_ENV || getEnvironment();

  posthog.register({
    organization,
    // org_id mirrors organization so PostHog dashboard breakdowns on 'org_id' resolve correctly
    org_id: organization,
    environment,
    team: "Manufacture-AI",
  });
}

/** Identify the current user. Call when auth state becomes authenticated. */
export function identifyUser(userId: string, traits?: Record<string, unknown>): void {
  if (!isEnabled()) return;
  try {
    posthog.identify(userId);
    if (traits && Object.keys(traits).length > 0) {
      posthog.people.set(traits);
    }
  } catch {
    // PostHog may not be initialized yet
  }
}

/** Track an event. No-op if PostHog is not initialized. */
export function track(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  if (!isEnabled()) return;
  try {
    posthog.capture(eventName, properties);
  } catch {
    // PostHog may not be initialized yet
  }
}
